import React, {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ReactNode,
} from 'react';

import PropTypes from 'prop-types';

import Button from '@adsk/alloy-react-button';
import { noop, Container } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import { stylePropType } from '@adsk/alloy-react-theme';

import Modal from '../Modal';

type Props = Omit<ComponentPropsWithRef<typeof Modal>, 'title'> & {
  className?: string;
  size?: 'small' | 'medium' | 'large' | 'extra_large';
  height?: string | number;
  modalProps?: ComponentPropsWithoutRef<typeof Modal>;
  show?: boolean;
  onClose?: () => void;
  onConfirm?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: ReactNode;
  message?: ReactNode;
  hideOnConfirm?: boolean;
  isOnConfirmInProgress?: boolean;
  isConfirmationButtonDisabled?: boolean;
  isConfirmationButtonAlert?: boolean;
  confirmationButtonLabel?: string;
  confirmationButtonClassName?: string;
  cancelButtonLabel?: string;
  noCancelButton?: boolean;
  disableModalExit?: boolean;
  container?: Container;
  locale?: string;
  backdrop?: 'static' | boolean;
};

/**
 * A modal component used for confirmation message.
 */
const ModalConfirmation = ({
  className,
  style,
  modalProps,
  size,
  height,
  show,
  title,
  message,
  cancelButtonLabel,
  confirmationButtonLabel,
  confirmationButtonClassName,
  backdrop,
  disableModalExit,
  isConfirmationButtonDisabled,
  isConfirmationButtonAlert,
  isOnConfirmInProgress,
  hideOnConfirm,
  noCancelButton,
  onClose,
  onConfirm,
  ...props
}: Props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  disableModalExit = disableModalExit || backdrop === 'static';
  const onConfirmHide = (e: React.MouseEvent<HTMLButtonElement>) => {
    onConfirm && onConfirm(e);
    hideOnConfirm && onClose && onClose();
  };

  return (
    <Modal
      data-testid="ModalConfirmation"
      className={className}
      style={style}
      open={show}
      hideOverlay={backdrop === false}
      onClose={onClose}
      size={size}
      height={height}
      {...modalProps}
      {...props}
    >
      {title && (
        <Modal.Header data-testid="ModalConfirmationHeader" disableBorder>
          {title}
          {!disableModalExit && <Modal.Close onClick={onClose} />}
        </Modal.Header>
      )}
      {message && (
        <Modal.Body data-testid="ModalConfirmationBody">{message}</Modal.Body>
      )}
      <Modal.Footer data-testid="ModalConfirmationFooter" disableBorder>
        {!noCancelButton && (
          <Button
            data-testid="ModalConfirmationFooter__cancel-button"
            style={{ marginRight: 8 }}
            onClick={onClose}
          >
            {cancelButtonLabel || T.translate('CANCEL')}
          </Button>
        )}
        <Button
          data-testid="ModalConfirmationFooter__confirmation-button"
          className={confirmationButtonClassName}
          onClick={onConfirmHide}
          variant={Button.VARIANTS.PRIMARY}
          loading={isOnConfirmInProgress}
          disabled={isConfirmationButtonDisabled}
          alert={isConfirmationButtonAlert}
        >
          {confirmationButtonLabel || T.translate('CONFIRM')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalConfirmation.SIZES = Modal.SIZES;
ModalConfirmation.defaultProps = {
  isConfirmationButtonAlert: true,
  backdrop: true,
  show: true,
  onConfirm: noop,
  height: 'auto',
  size: ModalConfirmation.SIZES.SMALL,
};

ModalConfirmation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Modal size */
  size: PropTypes.oneOf(Object.values(ModalConfirmation.SIZES)),
  /** modal height, default is auto */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Allows to pass props directly to the Modal component
   */
  modalProps: PropTypes.object,
  /**
   * A property (along with `onClose`) making this component an uncontrolled one.
   * When this prop isn't passed the component is controlled.
   * Controls whether or not to show the modal.
   */
  show: PropTypes.bool,
  /**
   * A property (along with `show`) making this component an uncontrolled one.
   * When this prop isn't passed the component is controlled.
   * Callback to when hiding the modal.
   */
  onClose: PropTypes.func,
  /**
   * A callback function for the modal confirm event (when clicking on the confirmation button)
   */
  onConfirm: PropTypes.func,
  /**
   * Text or a node to be displayed in the header of the modal
   */
  title: PropTypes.node,
  /**
   * Text or a node to be displayed in the body of the modal
   */
  message: PropTypes.node,
  /**
   * Whether or not the confirmation has progress or not.
   */
  hideOnConfirm: PropTypes.bool,
  /**
   * Whether modal buttons should be disabled
   */
  isOnConfirmInProgress: PropTypes.bool,
  /**
   * Whether confirmation button should be disabled
   */
  isConfirmationButtonDisabled: PropTypes.bool,
  /**
   * Whether confirmation button should be alert (red)
   */
  isConfirmationButtonAlert: PropTypes.bool,
  /**
   * Text for confirmation button
   */
  confirmationButtonLabel: PropTypes.string,
  /**
   * A custom CSS class for confirmation button
   */
  confirmationButtonClassName: PropTypes.string,
  /**
   * Text for cancel button (if not passed then will default to 'Cancel' based on locale)
   */
  cancelButtonLabel: PropTypes.string,
  /**
   * Whether the cancel button should not be displayed
   */
  noCancelButton: PropTypes.bool,
  /**
   * Determines if the modal cannot be exited (exit button will be hidden and clickoutside will be disabled)
   */
  disableModalExit: PropTypes.bool,
  /**
   * The modal container component. Used for attaching the modal as a child component to the container.
   */
  container: PropTypes.element,
  /**
   * The locale to use for the default cancel button label
   */
  locale: PropTypes.string,
  /**
   * Determines the backdrop of modal.
   */
  backdrop: PropTypes.oneOf(['static', true, false]),
};

export default ModalConfirmation;
